import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  MessageSquare,
  CalendarCheck,
  ClipboardList,
  BookOpen,
  CalendarDays,
  Boxes,
  Settings,
  Sun,
  Moon,
  LogOut,
  Menu,
  Bell,
  SlidersHorizontal,
  ShoppingCart,
  Store,
  FolderOpen,
  Crown,
  BellRing,
  ListTodo,
  ArrowLeftRight,
  CircleHelp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { AccentPicker } from "@/components/AccentPicker";
import { AppFooter } from "@/components/AppFooter";
import { ReminderSnoozeDialog } from "@/components/ReminderSnoozeDialog";
import { NotificationSettingsDialog } from "@/components/NotificationSettingsDialog";
import { TimetableOnboardingPrompt } from "@/components/TimetableOnboardingPrompt";
import { PageGuidanceProvider, usePageGuidance } from "@/components/common";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, ROLE_LABELS } from "@/lib/api";

const NAV = [
  {
    to: "/chat",
    label: "Ask Jonny",
    icon: MessageSquare,
    perm: "chat",
    id: "chat",
  },
  {
    to: "/bookings",
    label: "My bookings",
    icon: CalendarCheck,
    perm: "booking:create",
    id: "bookings",
    technicianHidden: true,
  },
  {
    to: "/prep",
    label: "Prep Room",
    icon: ClipboardList,
    perm: "booking:prep",
    id: "technician",
  },
  {
    to: "/practicals",
    label: "Practical library",
    icon: BookOpen,
    perm: "practical:read",
    id: "library",
  },
  {
    to: "/inventory",
    label: "Inventory",
    icon: Boxes,
    perm: "inventory:read",
    id: "inventory",
  },
  {
    to: "/tobuy",
    label: "To buy",
    icon: ShoppingCart,
    perm: "tobuy:manage",
    id: "tobuy",
    technicianHidden: true,
    adminPrepHidden: true,
  },
  {
    to: "/suppliers",
    label: "Supplier finder",
    icon: Store,
    perm: "suppliers:read",
    id: "suppliers",
    technicianHidden: true,
    adminPrepHidden: true,
    teacherHidden: true,
  },
  {
    to: "/documents",
    label: "Resources",
    icon: FolderOpen,
    perm: "documents:read",
    id: "documents",
  },
  {
    to: "/annual-reminders",
    label: "Annual reminders",
    icon: BellRing,
    perm: "annual_reminders:manage",
    id: "annual-reminders",
    technicianHidden: true,
    adminPrepHidden: true,
  },
  {
    to: "/my-todos",
    label: "My to-dos",
    icon: ListTodo,
    perm: "technician_tasks:manage",
    id: "my-todos",
    technicianHidden: true,
    adminPrepHidden: true,
  },
  {
    to: "/borrowed-equipment",
    label: "Borrowed equipment",
    icon: ArrowLeftRight,
    perm: "borrowed:manage",
    id: "borrowed-equipment",
    technicianHidden: true,
    adminPrepHidden: true,
  },
  {
    to: "/timetable",
    label: "Timetable",
    icon: CalendarDays,
    perm: "timetable:read_own",
    id: "timetable",
  },
  {
    to: "/admin",
    label: "Department admin",
    icon: Settings,
    perm: "users:manage",
    id: "admin",
  },
  {
    to: "/super",
    label: "Owner panel",
    icon: Crown,
    superOnly: true,
    id: "super",
  },
];

export function useTheme() {
  const [dark, setDark] = useState(
    () => localStorage.getItem("aj-theme") === "dark",
  );
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("aj-theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

export function Brand({ school, compact = false, testId = "sidebar-brand" }) {
  const logo = school?.branding?.logo || "/askjonnylogosmall.png";
  return (
    <div
      className={`flex items-center ${compact ? "gap-2" : "gap-3"}`}
      data-testid={testId}
    >
      <img
        src={logo}
        alt="Ask Jonny"
        className={`${compact ? "h-8 w-8" : "h-12 w-12"} rounded-full object-contain bg-white`}
        data-testid={`${testId}-logo`}
      />
      <div
        className={`${compact ? "text-base" : "text-xl"} font-display font-bold leading-none`}
      >
        Ask <span className="text-primary">Jonny</span>
      </div>
    </div>
  );
}

function Notifications() {
  const [items, setItems] = useState([]);
  const toastIds = useRef(new Set());
  const [snoozeNotification, setSnoozeNotification] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const isDueReminder = (item) => {
    const dueAt = item.data?.reminder_at;
    if (!dueAt) return true;
    const dueTime = new Date(dueAt).getTime();
    return Number.isNaN(dueTime) || dueTime <= Date.now();
  };
  useEffect(() => {
    try {
      toastIds.current = new Set(
        JSON.parse(sessionStorage.getItem("aj-reminder-toast-ids") || "[]"),
      );
    } catch {
      toastIds.current = new Set();
    }
  }, []);
  const load = useCallback(() => {
    return api
      .get("/notifications")
      .then((r) => {
        const nextItems = r.data;
        nextItems
          .filter((item) => item.type === "todo_reminder" && !item.read)
          .filter(isDueReminder)
          .filter((item) => !toastIds.current.has(item.id))
          .forEach((item) => {
            toast(item.title, {
              description: item.body,
              action: item.data?.task_id
                ? { label: "Snooze…", onClick: () => setSnoozeNotification(item) }
                : item.link
                  ? { label: "View", onClick: () => navigate(item.link) }
                  : undefined,
            });
            toastIds.current.add(item.id);
          });
        try {
          sessionStorage.setItem(
            "aj-reminder-toast-ids",
            JSON.stringify([...toastIds.current].slice(-100)),
          );
        } catch {
          // The unread bell alert remains available if browser storage is unavailable.
        }
        setItems(nextItems);
      })
      .catch(() => {});
  }, [navigate]);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  const unread = items.filter((n) => !n.read).length;
  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  return (
    <>
      <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notifications-button"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-rose-600 text-[11px] font-bold text-white grid place-items-center ring-2 ring-card"
              data-testid="notifications-unread-count"
            >
              {unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-sm">Notifications</span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              title="Notification settings"
              onClick={() => setSettingsOpen(true)}
              data-testid="notification-settings-open-button"
            >
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
            <button
              className="text-xs text-primary hover:underline"
              onClick={markAll}
              data-testid="notifications-mark-all"
            >
              Mark all read
            </button>
          </div>
        </div>
        <div
          className="max-h-96 overflow-y-auto"
          data-testid="notifications-list"
        >
          {items.length === 0 && (
            <p className="text-sm text-muted-foreground p-4">Nothing yet.</p>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => n.link && navigate(n.link)}
              className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-muted/60 transition-colors ${n.read ? "opacity-60" : ""}`}
              data-testid={`notification-${n.id}`}
            >
              <div className="text-sm font-medium">{n.title}</div>
              {n.body && (
                <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                  {n.body}
                </div>
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
      </Popover>
      <ReminderSnoozeDialog
        notification={snoozeNotification}
        onClose={() => setSnoozeNotification(null)}
        onSnoozed={(notificationId) => {
          setItems((current) =>
            current.map((item) => (
              item.id === notificationId ? { ...item, read: true } : item
            )),
          );
        }}
      />
      <NotificationSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
}

function SchoolCard({ user }) {
  return (
    <div
      className="rounded-xl border bg-card p-4 shadow-sm"
      data-testid="sidebar-school-card"
    >
      <div
        className="text-[13px] font-medium truncate"
        data-testid="sidebar-school-name"
      >
        {user.school?.name}
      </div>
      <div
        className="text-xs text-muted-foreground mt-1"
        data-testid="current-user-role"
      >
        {ROLE_LABELS[user.role]}
      </div>
      <div
        className="font-mono text-[11px] text-primary mt-3"
        data-testid="sidebar-week-info"
      >
        {user.school?.week ? `Week ${user.school.week}` : "No school day"}
      </div>
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = user.name
    .replace(/\(.*?\)/g, "")
    .split(/\s+/)
    .filter((w) => w && !/^(Mr|Mrs|Ms|Miss|Dr|Prof)\.?$/i.test(w))
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="h-8 w-8 rounded-full overflow-hidden ring-2 ring-border hover:ring-primary/50 transition-shadow bg-primary/15 text-primary text-xs font-bold grid place-items-center"
          data-testid="user-menu-button"
        >
          {user.picture ? (
            <img
              src={user.picture}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            initials
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div
            className="text-sm font-medium truncate"
            data-testid="current-user-name"
          >
            {user.name}
          </div>
          <div className="text-xs text-muted-foreground font-normal truncate">
            {user.email}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
          data-testid="logout-button"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function HeaderActions() {
  const [dark, setDark] = useTheme();
  const { user } = useAuth();
  const billing = user?.billing;
  const trial = billing?.plan === "trial";
  return (
    <div className="flex items-center gap-1">
      {trial && (
        <a
          href={`mailto:${user.billing.contact}`}
          data-testid="header-subscribe-link"
        >
          <Button variant="outline" size="sm" className="h-8 px-2.5 text-xs">
            Subscribe
          </Button>
        </a>
      )}
      <Notifications />
      <PageInstructions />
      <AccentPicker dark={dark} />
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setDark(!dark)}
        data-testid="theme-toggle-button"
        aria-label="Toggle theme"
      >
        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>
      <div className="ml-1">
        <UserMenu />
      </div>
    </div>
  );
}

function PageInstructions() {
  const [open, setOpen] = useState(false);
  const { guidance } = usePageGuidance();
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        aria-label="Open page instructions"
        data-testid="page-instructions-button"
      >
        <CircleHelp className="h-4 w-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="page-instructions-dialog">
          <DialogHeader>
            <DialogTitle data-testid="page-instructions-title">
              {guidance?.label || "This page"} instructions
            </DialogTitle>
            <DialogDescription className="sr-only">
              Page-specific instructions for the current screen.
            </DialogDescription>
          </DialogHeader>
          <p
            className="whitespace-pre-wrap text-sm leading-6 text-muted-foreground"
            data-testid="page-instructions-content"
          >
            {guidance?.instructions || "Page guidance is unavailable right now."}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SidebarBody({ onNav }) {
  const { user, can } = useAuth();
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 h-[68px] flex items-center">
        <Brand school={user.school} />
      </div>
      <nav className="flex-1 px-4 py-3 space-y-1.5">
        {NAV.filter((n) => (n.superOnly ? user.superadmin : can(n.perm)) && !(user.role === "technician" && n.technicianHidden) && !(user.role === "admin" && n.adminPrepHidden) && !(user.role === "teacher" && n.teacherHidden)).map(
          (n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={onNav}
              data-testid={`nav-${n.id}-link`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm border transition-colors ${isActive ? "bg-card border-border shadow-sm text-foreground font-medium" : "border-transparent text-muted-foreground hover:bg-card/70 hover:text-foreground"}`
              }
            >
              {({ isActive }) => (
                <>
                  <n.icon
                    className={`h-4 w-4 ${isActive ? "text-primary" : ""}`}
                  />{" "}
                  {n.label}
                </>
              )}
            </NavLink>
          ),
        )}
      </nav>
      <div className="p-4">
        <SchoolCard user={user} />
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const billing = user?.billing;
  const trial = billing?.plan === "trial";
  return (
    <PageGuidanceProvider>
      <div className="h-screen flex overflow-hidden bg-background">
        <aside className="hidden lg:block w-64 shrink-0 border-r bg-muted/40">
          <SidebarBody />
        </aside>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="left" className="p-0 w-72">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarBody onNav={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <main className="flex-1 flex flex-col min-w-0 h-screen">
          <header className="flex items-center gap-2 border-b px-3 h-12 bg-background">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="lg:hidden">
              <Brand school={user.school} compact testId="mobile-brand" />
            </div>
            {user.school?.is_demo && (
              <span
                className="hidden sm:inline-flex text-[11px] font-medium rounded-full px-2.5 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
                data-testid="demo-badge"
              >
                Demo school — test data
              </span>
            )}
            {trial && (
              <div
                className="hidden lg:flex items-center whitespace-nowrap text-[11px] text-muted-foreground ml-2"
                data-testid="header-trial-status"
              >
                <b className="text-foreground">Free trial</b>
                <span className="mx-1.5">·</span>
                <span>{billing.days_left} days left</span>
                <span className="mx-1.5">·</span>
                <span>
                  {Number(billing.credits_used || 0).toFixed(2)} of {billing.credit_limit} AI credits used
                </span>
                <span className="mx-1.5">·</span>
                <span>Typical question costs less than 1p</span>
              </div>
            )}
            <div className="ml-auto">
              <HeaderActions />
            </div>
          </header>
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            {children}
          </div>
          <AppFooter />
        </main>
        <TimetableOnboardingPrompt />
      </div>
    </PageGuidanceProvider>
  );
}
